import React, { useEffect, useState, useCallback } from 'react';

export const BackendHealthNotice: React.FC = () => {
  const [isOffline, setIsOffline] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '0.0.0.0');

  const checkHealth = useCallback(async () => {
    if (!isLocalhost) return;
    setIsChecking(true);
    try {
      // Check health endpoint directly
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3500);
      const res = await fetch('/health', { signal: controller.signal });
      clearTimeout(timeout);

      if (res.ok) {
        setIsOffline(false);
      } else {
        setIsOffline(true);
      }
    } catch {
      setIsOffline(true);
    } finally {
      setIsChecking(false);
    }
  }, [isLocalhost]);

  useEffect(() => {
    if (!isLocalhost) return;

    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, [checkHealth, isLocalhost]);

  if (!isLocalhost || !isOffline || isDismissed) {
    return null;
  }

  return (
    <aside
      aria-label="Backend Server Offline Alert"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 999999,
        background: 'linear-gradient(90deg, #7F1D1D, #991B1B)',
        color: '#FFFFFF',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '13px',
        fontWeight: 500,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '18px' }}>⚠️</span>
        <div>
          <strong style={{ fontWeight: 700 }}>Localhost Backend Offline:</strong> Cannot connect to Express API on{' '}
          <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>
            http://localhost:3001
          </code>
          . Please start the backend server by running{' '}
          <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', color: '#FEF08A' }}>
            npm run dev:all
          </code>{' '}
          or{' '}
          <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', color: '#FEF08A' }}>
            npm run backend:dev
          </code>
          .
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <button
          type="button"
          onClick={checkHealth}
          disabled={isChecking}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            color: '#FFFFFF',
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: isChecking ? 'wait' : 'pointer',
            transition: 'background 0.15s',
          }}
        >
          {isChecking ? 'Checking...' : '🔄 Retry Connection'}
        </button>

        <button
          type="button"
          onClick={() => setIsDismissed(true)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '16px',
            cursor: 'pointer',
            padding: '4px 8px',
          }}
          title="Dismiss notification"
        >
          ✕
        </button>
      </div>
    </aside>
  );
};
